import { useCallback, useEffect, useRef, useState } from 'react';
import { gamesApi, getSocket, onSocketJoin } from '../../services/api/gamesApi';

/**
 * Ludo presence: dashboard uses watch mode (visible in online list + receives invites).
 * Ludo lobby uses lobby mode (same online list + stake availability).
 */
export function useLudoPresence({
  enabled = true,
  joinLobby = true,
  autoResume = false,
  availableStake = null,
  onMatchFound,
  onInviteReceived,
}) {
  const [online, setOnline] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const onMatchRef = useRef(onMatchFound);
  const onInviteRef = useRef(onInviteReceived);

  onMatchRef.current = onMatchFound;
  onInviteRef.current = onInviteReceived;

  const refreshOnline = useCallback(async () => {
    try {
      const o = await gamesApi.ludoOnline();
      setOnline(o.users ?? []);
    } catch {
      /* ignore */
    }
  }, []);

  const refreshInvites = useCallback(async () => {
    try {
      const s = await gamesApi.ludoState();
      setPendingInvites(s.pendingInvites ?? []);
      if (autoResume && s.activeRoom?.status === 'PLAYING') {
        onMatchRef.current?.(s.activeRoom.id);
      }
      return s;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refreshOnline(), refreshInvites()]);
    } finally {
      setRefreshing(false);
    }
  }, [refreshOnline, refreshInvites]);

  useEffect(() => {
    if (!enabled) {
      getSocket().emit(joinLobby ? 'leave:ludo-lobby' : 'leave:ludo-watch');
      return undefined;
    }

    refresh();

    const cleanup = onSocketJoin((socket) => {
      if (joinLobby) {
        socket.emit('join:ludo-lobby', { availableStake });
      } else {
        socket.emit('join:ludo-watch');
      }
    });

    const socket = getSocket();
    const onLobbyUpdate = () => {
      refreshOnline();
    };
    const onInvite = (payload) => {
      refreshInvites();
      onInviteRef.current?.(payload);
    };
    const onMatch = async (payload) => {
      if (!payload?.roomId) return;
      try {
        const s = await gamesApi.ludoState();
        if (s.activeRoom?.id === payload.roomId && s.activeRoom?.status === 'PLAYING') {
          onMatchRef.current?.(payload.roomId);
        }
      } catch {
        /* ignore */
      }
    };

    socket.on('ludo:lobby-update', onLobbyUpdate);
    socket.on('ludo:invite', onInvite);
    socket.on('ludo:match-found', onMatch);

    return () => {
      cleanup();
      socket.emit(joinLobby ? 'leave:ludo-lobby' : 'leave:ludo-watch');
      socket.off('ludo:lobby-update', onLobbyUpdate);
      socket.off('ludo:invite', onInvite);
      socket.off('ludo:match-found', onMatch);
    };
  }, [enabled, joinLobby, autoResume, availableStake, refresh, refreshOnline, refreshInvites]);

  const sendInvite = useCallback(async (toUserId, stake) => {
    setLoading(true);
    try {
      await gamesApi.ludoInvite(toUserId, stake);
      await refresh();
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  const respondInvite = useCallback(async (inviteId, accept) => {
    setLoading(true);
    try {
      const result = await gamesApi.ludoRespondInvite(inviteId, accept);
      await refresh();
      if (result.accepted && result.room?.id) {
        onMatchRef.current?.(result.room.id);
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  return {
    online,
    pendingInvites,
    loading,
    refreshing,
    refresh,
    sendInvite,
    respondInvite,
  };
}
